import { ServerSideEventError } from './ServerSideEventError';
import type { EventType, ServerSideEventListener, ServerSideEventSubscribe } from './types';

export type ServerSideEventSubscriptions = {
    subscribe: ServerSideEventSubscribe;
    publish: (event: EventType) => void;
    clear: () => void;
};

/** Synchronous delivery only: consumers own their asynchronous processing queues. */
export const makeServerSideEventSubscriptions = (
    reportError: (error: ServerSideEventError) => void
): ServerSideEventSubscriptions => {
    const listeners = new Set<ServerSideEventListener>();
    const subscribe: ServerSideEventSubscribe = (listener) => {
        // Each registration owns its cleanup even when callers share a callback.
        const subscription: ServerSideEventListener = (event) => {
            listener(event);
        };
        listeners.add(subscription);
        const unsubscribe = () => {
            listeners.delete(subscription);
        };
        return unsubscribe;
    };
    const publish = (event: EventType) => {
        const currentListeners = Array.from(listeners);
        for (const listener of currentListeners) {
            if (!listeners.has(listener)) continue;
            try {
                listener(event);
            } catch (cause) {
                const error = new ServerSideEventError('subscriber', cause);
                reportError(error);
            }
        }
    };
    const clear = () => {
        listeners.clear();
    };
    const subscriptions = { subscribe, publish, clear };
    return subscriptions;
};
